/**
 * Shoppable Products Section
 *
 * Handles multi-product colour selection and variant picking for landing pages.
 * Each product represents a colour, with size variants.
 *
 * Events dispatched:
 * - on:variant:change - When a size variant is selected
 * - on:cart:add - On successful add to cart
 * - on:cart:error - On add to cart failure
 */

if (!customElements.get('shoppable-products')) {
  class ShoppableProducts extends HTMLElement {
    constructor() {
      super();
      this.selectedProductIndex = null;
      this.selectedVariantId = null;

      if (window.initLazyScript) {
        window.initLazyScript(this, this.init.bind(this));
      } else {
        this.init();
      }
    }

    init() {
      // Get DOM elements
      this.imageItems = this.querySelectorAll('.shoppable-products__image-item');
      this.imageButtons = this.querySelectorAll('.shoppable-products__image-btn');
      this.swatchButtons = this.querySelectorAll('.shoppable-products__swatch');
      this.sizesContainer = this.querySelector('[data-sizes-container]');
      this.sizeFieldset = this.querySelector('.shoppable-products__size-fieldset');
      this.sizeButtonsContainer = this.querySelector('.shoppable-products__size-buttons');
      this.priceContainer = this.querySelector('[data-price-container]');
      this.form = this.querySelector('.shoppable-products__form');
      this.variantInput = this.querySelector('[data-variant-id-input]');
      this.addButton = this.querySelector('.shoppable-products__add-btn');

      // Carousel scroll controls
      this.carousel = this.querySelector('.shoppable-products__carousel');
      this.imagesTrack = this.querySelector('.shoppable-products__images');
      this.navPrev = this.querySelector('[data-nav-prev]');
      this.navNext = this.querySelector('[data-nav-next]');

      // Parse product data
      const jsonEl = this.querySelector('[data-products-json]');
      if (!jsonEl) {
        // No products yet - watch for content changes (AJAX loading)
        this.observeForContent();
        return;
      }

      try {
        this.productsData = JSON.parse(jsonEl.textContent).products;
      } catch (e) {
        console.error('ShoppableProducts: Failed to parse product data', e);
        return;
      }

      // Bind events
      this.bindEvents();

      // Set up prev/next carousel scrolling
      this.setupCarousel();

      this.setAttribute('loaded', '');
    }

    /**
     * Watches for content to be added via AJAX and reinitializes
     */
    observeForContent() {
      if (this.contentObserver) return; // Already observing

      this.contentObserver = new MutationObserver((mutations) => {
        const jsonEl = this.querySelector('[data-products-json]');
        if (jsonEl) {
          // Products loaded - disconnect observer and reinitialize
          this.contentObserver.disconnect();
          this.contentObserver = null;
          this.init();
        }
      });

      this.contentObserver.observe(this, {
        childList: true,
        subtree: true
      });
    }

    bindEvents() {
      // Image click handlers (on container and button for reliable capture)
      this.imageItems.forEach((item, index) => {
        item.addEventListener('click', (e) => {
          e.preventDefault();
          this.selectProduct(index);
        });
      });

      this.imageButtons.forEach((btn, index) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation(); // Prevent double-firing from container
          this.selectProduct(index);
        });
      });

      // Swatch click handlers
      this.swatchButtons.forEach((btn, index) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.selectProduct(index);
        });
      });

      // Form submission
      if (this.form && theme && theme.settings && theme.settings.afterAddToCart !== 'no-js') {
        this.form.addEventListener('submit', this.handleSubmit.bind(this));
      }
    }

    /**
     * Sets up the prev/next scroll arrows. Arrows are only revealed (via the
     * `has-overflow` class) when the image track actually overflows.
     */
    setupCarousel() {
      if (!this.carousel || !this.imagesTrack || !this.navPrev || !this.navNext) return;

      this.navPrev.addEventListener('click', () => this.scrollTrack(-1));
      this.navNext.addEventListener('click', () => this.scrollTrack(1));
      this.imagesTrack.addEventListener('scroll', () => this.updateNavState(), { passive: true });

      // Theme dispatches a debounced resize event we can piggyback on.
      window.addEventListener('on:debounced-resize', () => this.updateNavState());

      this.updateNavState();
    }

    /**
     * Scrolls the image track by roughly one viewport width.
     * @param {number} direction - -1 for previous, 1 for next
     */
    scrollTrack(direction) {
      const amount = this.imagesTrack.clientWidth * 0.8 * direction;
      this.imagesTrack.scrollBy({ left: amount, behavior: 'smooth' });
    }

    /**
     * Shows/hides arrows based on overflow and disables them at scroll extents.
     */
    updateNavState() {
      if (!this.carousel || !this.imagesTrack) return;

      const track = this.imagesTrack;
      const maxScroll = track.scrollWidth - track.clientWidth;
      const hasOverflow = maxScroll > 1;

      this.carousel.classList.toggle('has-overflow', hasOverflow);
      if (!hasOverflow) return;

      this.navPrev.disabled = track.scrollLeft <= 1;
      this.navNext.disabled = track.scrollLeft >= maxScroll - 1;
    }

    /**
     * Selects a product (colour) and updates the UI
     * @param {number} index - Product index
     */
    selectProduct(index) {
      if (this.selectedProductIndex === index) return;

      this.selectedProductIndex = index;
      const product = this.productsData[index];

      // Update visual selection states
      this.updateSelectionUI(index);

      // Scroll image into view if needed
      const selectedItem = this.imageItems[index];
      if (selectedItem) {
        selectedItem.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }

      // Render size buttons for this product
      this.renderSizeButtons(product);

      // Update price display
      this.updatePrice(product);

      // Reset variant selection
      this.selectedVariantId = null;
      this.updateAddButton(false, product);
    }

    /**
     * Updates visual selection states for images and swatches
     * @param {number} selectedIndex - Selected product index
     */
    updateSelectionUI(selectedIndex) {
      // Update image items
      this.imageItems.forEach((item, i) => {
        item.classList.toggle('is-selected', i === selectedIndex);
      });

      // Update swatches
      this.swatchButtons.forEach((swatch, i) => {
        swatch.classList.toggle('is-selected', i === selectedIndex);
        swatch.setAttribute('aria-pressed', i === selectedIndex ? 'true' : 'false');
      });
    }

    /**
     * Renders size buttons for the selected product
     * @param {Object} product - Product data object
     */
    renderSizeButtons(product) {
      // Enable the fieldset
      if (this.sizeFieldset) {
        this.sizeFieldset.disabled = false;
      }

      // Clear existing buttons
      this.sizeButtonsContainer.innerHTML = '';

      // Create size buttons
      product.variants.forEach((variant) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'shoppable-products__size-btn';
        btn.textContent = this.formatSizeLabel(variant.option1);
        btn.dataset.variantId = variant.id;
        btn.disabled = !variant.available;

        if (!variant.available) {
          btn.classList.add('is-unavailable');
          btn.title = theme && theme.strings ? theme.strings.noStock : 'Sold out';
        }

        btn.addEventListener('click', () => this.selectVariant(variant, btn));

        this.sizeButtonsContainer.appendChild(btn);
      });
    }

    /**
     * Formats size labels (e.g., "Extra Small" -> "XS")
     * @param {string} size - Original size value
     * @returns {string} Formatted size label
     */
    formatSizeLabel(size) {
      const sizeMap = {
        'XXS': '2XS',
        'Extra Small': 'XS',
        'Small': 'S',
        'Medium': 'M',
        'Large': 'L',
        'Extra Large': 'XL',
        'XXL': '2XL',
        'XXXL': '3XL',
        'XXXXL': '4XL'
      };
      return sizeMap[size] || size;
    }

    /**
     * Selects a variant (size)
     * @param {Object} variant - Variant data object
     * @param {Element} btn - Clicked button element
     */
    selectVariant(variant, btn) {
      // Update selection UI
      this.querySelectorAll('.shoppable-products__size-btn').forEach((b) => {
        b.classList.remove('is-selected');
        b.setAttribute('aria-pressed', 'false');
      });
      btn.classList.add('is-selected');
      btn.setAttribute('aria-pressed', 'true');

      // Store variant ID
      this.selectedVariantId = variant.id;

      // Update form input
      if (this.variantInput) {
        this.variantInput.value = variant.id;
        this.variantInput.disabled = false;
      }

      // Update price if variant has different price
      const product = this.productsData[this.selectedProductIndex];
      this.updatePrice(product, variant);

      // Enable add button
      this.updateAddButton(true);

      // Dispatch variant change event
      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          variant: variant,
          product: product,
          form: this.form
        }
      }));
    }

    /**
     * Updates the price display
     * @param {Object} product - Product data
     * @param {Object} variant - Optional variant data
     */
    updatePrice(product, variant = null) {
      if (!this.priceContainer) return;

      const data = variant || product;
      const price = data.priceFormatted;
      const compareAtPrice = data.compareAtPriceFormatted;
      const onSale = variant ? data.onSale : (data.compareAtPrice > data.price);

      let priceHtml = '';
      if (onSale && compareAtPrice) {
        priceHtml = `<span class="shoppable-products__price-compare">${compareAtPrice}</span><span class="shoppable-products__price-value">${price}</span>`;
        this.priceContainer.classList.add('shoppable-products__price--on-sale');
      } else {
        priceHtml = `<span class="shoppable-products__price-value">${price}</span>`;
        this.priceContainer.classList.remove('shoppable-products__price--on-sale');
      }

      this.priceContainer.innerHTML = priceHtml;
    }

    /**
     * Updates the Add to Cart button state
     * @param {boolean} enabled - Whether button should be enabled
     * @param {Object} product - Optional product data
     */
    updateAddButton(enabled, product = null) {
      if (!this.addButton) return;

      this.addButton.disabled = !enabled;

      const addToCartText = this.addButton.dataset.addToCartText || 'Add to Cart';

      if (!enabled && this.selectedProductIndex !== null) {
        this.addButton.textContent = 'Select a size';
      } else if (!enabled) {
        this.addButton.textContent = addToCartText;
      } else {
        this.addButton.textContent = addToCartText;
      }
    }

    /**
     * Handles form submission (add to cart)
     * @param {Event} evt - Submit event
     */
    async handleSubmit(evt) {
      evt.preventDefault();

      if (!this.selectedVariantId) return;

      this.addButton.disabled = true;
      this.addButton.classList.add('is-loading');

      const formData = new FormData(this.form);

      // Get section IDs for updating cart drawer/header
      const sectionsToUpdate = ['page-header', 'cart-drawer']
        .map((sel) => document.querySelector(sel))
        .filter((el) => el && el.dataset && el.dataset.sectionId);
      const sectionIds = sectionsToUpdate.map((el) => el.dataset.sectionId);

      formData.append('sections_url', window.location.pathname);
      if (sectionIds.length > 0) {
        formData.append('sections', sectionIds.join(','));
      }

      try {
        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            'Accept': 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.description || data.message || 'Error adding to cart');
        }

        // Update cart sections
        if (data.sections) {
          sectionsToUpdate.forEach((el) => {
            if (el.updateFromCartChange && data.sections[el.dataset.sectionId]) {
              el.updateFromCartChange(data.sections[el.dataset.sectionId]);
            }
          });
        }

        // Handle after add behavior
        if (theme.settings.afterAddToCart === 'drawer') {
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
        } else if (theme.settings.afterAddToCart === 'notification') {
          // Theme handles notification display
        }

        // Dispatch success event
        this.dispatchEvent(new CustomEvent('on:cart:add', {
          bubbles: true,
          detail: {
            variantId: this.selectedVariantId,
            product: this.productsData[this.selectedProductIndex]
          }
        }));

      } catch (error) {
        console.error('ShoppableProducts: Add to cart error', error);

        // Show error message
        if (theme.showQuickPopup) {
          theme.showQuickPopup(error.message, this.addButton);
        }

        // Dispatch error event
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: { error: error.message }
        }));
      } finally {
        this.addButton.disabled = false;
        this.addButton.classList.remove('is-loading');
      }
    }
  }

  customElements.define('shoppable-products', ShoppableProducts);
}
